import { useEffect, useState } from 'react';
import { Clock, ArrowRight } from 'lucide-react';
import { CoverImage } from '../CoverImage';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return ''; }
};

export const InsightsTeaser = () => {
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/articles/public`)
      .then((r) => (r.ok ? r.json() : []))
      .then((all) => setArticles((all || []).slice(0, 3)))
      .catch(() => setArticles([]));
  }, []);

  if (!articles || articles.length === 0) return null;

  return (
    <section className="section it-section" id="insights" data-testid="home-insights-teaser">
      <div className="container">
        <div className="heading">
          <p className="eyebrow" style={{ color: 'var(--orange)' }}>Insights</p>
          <h2>Latest from Insights</h2>
          <p>Practical reads on Tableau, Power BI, and getting dashboards people actually use.</p>
        </div>

        <div className="it-grid" data-testid="home-insights-grid">
          {articles.map((a, i) => (
            <a key={a.slug} href={`/insights/${a.slug}`} className="it-card" data-testid={`home-insights-card-${i}`}>
              <div className="it-thumb">
                {a.cover_image_url && <CoverImage url={a.cover_image_url} alt={a.title} sizes="(max-width: 640px) 90vw, 360px" />}
                {a.category && <span className="it-cat">{a.category}</span>}
              </div>
              <div className="it-body">
                <h3>{a.title}</h3>
                {a.excerpt && <p className="it-excerpt">{a.excerpt}</p>}
                <div className="it-meta">
                  {a.published_at && <span>{formatDate(a.published_at)}</span>}
                  <span aria-hidden="true">·</span>
                  <span className="it-rt"><Clock className="w-3 h-3" /> {a.read_time_minutes} min read</span>
                </div>
                <span className="it-read">Read article <ArrowRight className="w-4 h-4" /></span>
              </div>
            </a>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
          <a className="case-button" href="/insights" data-testid="home-insights-view-all">View all insights &rarr;</a>
          <p style={{ marginTop: '1.25rem', fontSize: '.95rem', color: '#475569' }} data-testid="home-insights-cost-note">
            Planning a dashboard project? <a href="/insights/tableau-dashboard-cost-2026" style={{ color: '#003A70', fontWeight: 800 }}>See what a Tableau dashboard costs in 2026</a>.
          </p>
        </div>
      </div>

      <style>{`
        .redesign .it-section{background:#f7f9fc}
        .redesign .it-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:28px}
        .redesign .it-card{display:flex;flex-direction:column;border:1px solid var(--line);border-radius:16px;overflow:hidden;background:#fff;text-decoration:none;transition:border-color .25s,box-shadow .25s,transform .25s}
        .redesign .it-card:hover{border-color:var(--orange);box-shadow:0 18px 44px -14px rgba(30,58,138,.28);transform:translateY(-4px)}
        .redesign .it-thumb{position:relative;aspect-ratio:16/10;background:#eef2f7;display:grid;place-items:center;overflow:hidden}
        .redesign .it-thumb img{width:100%;height:100%;object-fit:cover;transition:transform .5s}
        .redesign .it-card:hover .it-thumb img{transform:scale(1.05)}
        .redesign .it-cat{position:absolute;top:12px;left:12px;background:var(--navy);color:#fff;font-size:.66rem;font-weight:900;text-transform:uppercase;letter-spacing:.06em;padding:5px 11px;border-radius:99px}
        .redesign .it-body{padding:22px 22px 24px;display:flex;flex-direction:column;flex:1}
        .redesign .it-body h3{font-size:1.16rem;line-height:1.32;color:#003A70;font-weight:900;margin:0;letter-spacing:-.02em}
        .redesign .it-card:hover .it-body h3{color:var(--navy)}
        .redesign .it-excerpt{font-size:.95rem;line-height:1.6;color:#475569;margin:12px 0 0;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
        .redesign .it-meta{display:flex;flex-wrap:wrap;align-items:center;gap:8px;font-size:.8rem;color:#94a3b8;margin-top:16px}
        .redesign .it-meta .it-rt{display:inline-flex;align-items:center;gap:5px}
        .redesign .it-read{display:inline-flex;align-items:center;gap:7px;margin-top:18px;font-size:.88rem;font-weight:900;color:var(--orange)}
        .redesign .it-read svg{transition:transform .25s}
        .redesign .it-card:hover .it-read svg{transform:translateX(4px)}
        @media(max-width:1000px){.redesign .it-grid{grid-template-columns:repeat(2,1fr)}}
        @media(max-width:680px){.redesign .it-grid{grid-template-columns:1fr}}
      `}</style>
    </section>
  );
};
